import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { TRANSACTION_STATUS } from '@app/constants/property';
import { cn, humanShorten, timeAgo } from '@app/libs/utils';

import Avatar from '../dashboard/Avatar';
import AreaIcon from '../icons/AreaIcon';
import ArrowDownIcon from '../icons/ArrowDownIcon';
import BathIcon from '../icons/BathIcon';
import BedIcon from '../icons/BedIcon';
import CheckedIcon from '../icons/CheckedIcon';

import { Thumbnail } from './Thumbnail';

interface PropertiesProps {
  className?: string;
  properties: any[];
  mode?: 'grid' | 'list' | 'scroll';
  loading?: boolean;
  itemClassName?: string;
}

export const Properties: React.FC<PropertiesProps> = ({
  className,
  properties,
  mode,
  loading,
  itemClassName,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState<any>({
    left: false,
    right: false,
  });

  const handleScroll = (direction: 'left' | 'right') => {
    if (mode !== 'scroll' || !containerRef.current) return;

    const item = containerRef.current.querySelector('.property');
    const width = item?.clientWidth || 0;

    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: direction === 'left' ? -width : width,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const checkScrollable = () => {
      const conatinerWidth = containerRef.current?.clientWidth || 0;
      const scrollLeft = containerRef.current?.scrollLeft || 0;
      setScrollable({
        left: scrollLeft > 0,
        right:
          (containerRef.current?.scrollLeft || 0) + conatinerWidth <
          (containerRef.current?.scrollWidth || 0),
      });
    };
    checkScrollable();

    containerRef.current?.addEventListener('scroll', checkScrollable);

    return () => {
      containerRef.current?.removeEventListener('scroll', checkScrollable);
    };
  }, [containerRef.current]);

  if (loading) {
    return (
      <div className={cn('2xl:w-[90rem] w-full mx-auto px-5 flex flex-col gap-5', className)}>
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-gray-800 text-lg">
            Chúng tôi đang tìm kiếm bất động sản phù hợp với tiêu chí đã lọc
          </p>
          <span className="text-gray-500 text-sm">Xin vui lòng đợi trong giây lát...</span>
        </div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className={cn('2xl:w-[90rem] w-full mx-auto px-5 flex flex-col gap-5', className)}>
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-gray-800 text-lg">
            Hiện tại chúng tôi chưa có bất động sản nào phù hợp với tiêu chí đã lọc
          </p>
          <span className="text-gray-500 text-sm">Xin vui lòng thử lại với các tiêu chí khác</span>
        </div>
      </div>
    );
  }

  if (mode === 'list') {
    return (
      <div className={cn('2xl:w-[90rem] w-full mx-auto px-5 flex flex-col gap-5', className)}>
        {properties.map(prop => (
          <div
            key={prop.id}
            className="relative flex flex-col md:flex-row bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer"
          >
            <div className="relative w-full h-80 md:h-auto md:w-[410px] md:max-w-[33vw] md:aspect-[410/274] border-r border-gray-200">
              <Thumbnail
                images={prop.images}
                alt={prop.title}
                width={410}
                height={274}
                imageClassName="object-contain md:object-cover"
              />
            </div>
            <div className="flex flex-col gap-4 grow p-5">
              <h3
                className="m-0 text-md font-semibold text-gray-900"
                dangerouslySetInnerHTML={{ __html: prop.title }}
              ></h3>
              <div className="flex gap-2 items-center">
                <span className="text-lg font-semibold text-blue-800">
                  {humanShorten(prop.price)}
                </span>
                <span className="text-xs text-gray-500 text-nowrap">&#9670;</span>
                <span className="text-sm text-gray-500 text-nowrap">
                  {humanShorten(Math.ceil(prop.price / prop.area)) || '-'}/m²
                </span>
                {prop.status === 'ACTIVE' && (
                  <>
                    <span className="text-xs text-gray-500 text-nowrap">&#9670;</span>
                    <span className="text-sm text-green-800 text-nowrap flex items-center gap-2">
                      <CheckedIcon className="w-4 h-4" />
                      Tin đã xác thực bởi PropX
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-start gap-2 text-gray-500">
                <Image
                  src="/images/figma/properties/icon-location.svg"
                  alt="location"
                  width={12}
                  height={12}
                />
                <p
                  className="m-0 text-sm text-truncate text-ellipsis line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: prop.address }}
                ></p>
              </div>
              <ul className="flex items-center gap-5 text-sm">
                <li className="flex text-sm text-gray-800 gap-2">
                  <BedIcon size={16} />
                  <span className="">{prop.bedrooms || '-'}</span>
                </li>
                <li className="flex text-sm text-gray-800 gap-2">
                  <BathIcon size={16} />
                  <span className="">{prop.bathrooms || '-'}</span>
                </li>
                <li className="flex text-sm text-gray-800 gap-2">
                  <AreaIcon size={16} />
                  <span className="">{prop.area || '-'} m²</span>
                </li>
              </ul>
              <div className="flex items-center gap-2 text-sm grow" />
              <div className="flex items-center gap-2 text-sm border-t border-gray-200 pt-4">
                <Avatar
                  userInfo={{ ...prop.contactInformation, avatar: prop.avatar || 'text' }}
                  width={24}
                  height={24}
                  className="w-8 h-8 rounded-full overflow-hidden text-lg bg-gray-200"
                />
                <div className="flex flex-col gap-1">
                  <span className="text-gray-800">
                    {prop.contactInformation.alias ||
                      prop.contactInformation.fullName ||
                      prop.contactInformation.name}
                  </span>
                  <span className="text-xs text-gray-500">{timeAgo(prop.createdAt)}</span>
                </div>
              </div>
            </div>
            <Link
              href={`/properties/${prop.id}`}
              className="absolute top-0 left-0 h-full w-full p-4 opacity-0"
            >
              Chi tiết
            </Link>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative">
      {mode === 'scroll' && (scrollable.left || scrollable.right) && (
        <div className="absolute top-0 right-5 h-10 w-10 z-10 flex gap-5 -translate-y-14">
          <button
            className={cn(
              'h-10 w-10 z-10',
              scrollable.left ? 'opacity-100  cursor-pointer' : 'opacity-50 cursor-not-allowed'
            )}
            onClick={() => handleScroll('left')}
            disabled={!scrollable.left}
          >
            <ArrowDownIcon className="w-6 h-6 rotate-90" />
          </button>
          <button
            className={cn(
              'h-10 w-10 z-10',
              scrollable.right ? 'opacity-100  cursor-pointer' : 'opacity-50 cursor-not-allowed'
            )}
            onClick={() => handleScroll('right')}
            disabled={!scrollable.right}
          >
            <ArrowDownIcon className="w-6 h-6 rotate-270" />
          </button>
        </div>
      )}

      <div
        className={cn(
          '2xl:w-[90rem] w-full mx-auto px-5 gap-5',
          mode === 'grid' && 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4',
          mode === 'scroll' &&
            'flex gap-5 flex-nowrap overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4 relative',
          className
        )}
        ref={containerRef}
      >
        {properties.map(prop => (
          <div
            key={prop.id}
            className={cn(
              'property relative flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer',
              mode === 'scroll' && 'snap-center shrink-0',
              itemClassName
            )}
          >
            <div className="relative w-full h-[274px] border-b border-gray-200">
              <Thumbnail images={prop.images} alt={prop.title} width={410} height={274} />
              <div className="flex gap-2 absolute top-2 right-2">
                {prop.status === 'ACTIVE' ? (
                  <span className="text-green-600 text-xs px-2 py-1 bg-green-50 border border-green-200 rounded-lg">
                    Sẵn sàng giao dịch
                  </span>
                ) : (
                  <span className="text-yellow-600 text-xs px-2 py-1 bg-yellow-50 border border-yellow-200 rounded-lg">
                    {
                      TRANSACTION_STATUS.find(status => status.value === (prop.status as any))
                        ?.label
                    }
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-4 p-4 grow justify-start">
              <div className="flex flex-col gap-4 grow">
                <div className="w-full grow overflow-hidden flex flex-col gap-2">
                  <span className="text-xs text-nowrap border border-blue-500 text-blue-500 rounded-full px-2 py-1 w-fit">
                    {prop.provinceName}
                  </span>
                  <h3
                    className="m-0 text-md font-semibold text-gray-900 gap-1 text-truncate text-ellipsis line-clamp-2 max-w-full"
                    dangerouslySetInnerHTML={{ __html: prop.title }}
                  ></h3>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-lg font-bold text-blue-800">
                    {humanShorten(prop.price)}
                  </span>
                  <span className="text-xs text-gray-500 text-nowrap">&#9670;</span>
                  <span className="text-sm text-gray-800 text-nowrap">{prop.area} m²</span>
                </div>
                <div className="flex items-start gap-1">
                  <Image
                    src="/images/figma/properties/icon-location.svg"
                    alt="location"
                    width={12}
                    height={12}
                  />
                  <p
                    className="m-0 text-sm text-gray-800 text-truncate text-ellipsis line-clamp-1"
                    dangerouslySetInnerHTML={{ __html: prop.address }}
                  ></p>
                </div>
              </div>
            </div>
            <Link
              href={`/properties/${prop.id}`}
              className="absolute top-0 left-0 h-full w-full p-4 opacity-0"
            >
              Chi tiết
            </Link>
            <div className="flex items-center gap-2 text-sm border-t border-gray-200 p-4">
              <Avatar
                userInfo={{ ...prop.contactInformation, avatar: prop.avatar || 'text' }}
                width={24}
                height={24}
                className="w-8 h-8 rounded-full overflow-hidden text-lg bg-gray-200"
              />
              <div className="flex flex-col gap-1">
                <span className="text-gray-800">
                  {prop.contactInformation.alias ||
                    prop.contactInformation.fullName ||
                    prop.contactInformation.name}
                </span>
                <span className="text-xs text-gray-500">{timeAgo(prop.createdAt)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
